import type { EulerTuple, QuaternionTuple, Vector3Tuple } from 'three';

export const MPS_TO_KPH = 3.6;

const PI = Math.PI;
const HALF_PI = PI / 2;
const TWO_PI = PI * 2;

export const toRadian = (degree: number) => (degree / 180) * Math.PI;

export const toDegree = (radian: number) => (radian / Math.PI) * 180;

export const convertNegativeRadianToPositive = (radian: number) =>
  radian < 0 ? TWO_PI - (Math.abs(radian) % TWO_PI) : radian % TWO_PI;

export const getMinAdjustRotationRadian = (radian: number) => {
  const rad = convertNegativeRadianToPositive(radian);
  const diffRadList = Array.from({ length: 5 }, (_, i) => i * HALF_PI - rad);
  const resultRad = diffRadList
    .reduce(
      ([minDiffRad, dir], diffRad) => {
        const absDiffDeg = Math.abs(diffRad);

        if (absDiffDeg < minDiffRad) {
          minDiffRad = absDiffDeg;
          dir = diffRad < 0 ? -1 : 1;
        }

        return [minDiffRad, dir];
      },
      [TWO_PI, 1],
    )
    .reduce((acc, cur) => acc * cur, 1);

  return resultRad;
};

export const containsPoint = (
  {
    max,
    min,
  }: {
    max: Vector3Tuple;
    min: Vector3Tuple;
  },
  position: Vector3Tuple,
) => position.every((v, i) => min[i] <= v && v <= max[i]);

export const getDiagonalLength = (...numbers: [number, number]) =>
  Math.sqrt(numbers.reduce((acc, cur) => acc + cur ** 2, 0));

export const normalizeQuaternion = (q: QuaternionTuple) => {
  const length = Math.sqrt(q.reduce((acc, v) => acc + v ** 2, 0));

  return q.map(v => v / length) as QuaternionTuple;
};

export const conjugateQuaternion = (q: QuaternionTuple) =>
  q.map((v, i) => (i < q.length - 1 ? -v : v)) as QuaternionTuple;

export const multiplyQuaternion = (
  q1: QuaternionTuple,
  q2: QuaternionTuple,
): QuaternionTuple => {
  const [q1X, q1Y, q1Z, q1W] = q1;
  const [q2X, q2Y, q2Z, q2W] = q2;

  const x = q1W * q2X + q1X * q2W + q1Y * q2Z - q1Z * q2Y;
  const y = q1W * q2Y - q1X * q2Z + q1Y * q2W + q1Z * q2X;
  const z = q1W * q2Z + q1X * q2Y - q1Y * q2X + q1Z * q2W;
  const w = q1W * q2W - q1X * q2X - q1Y * q2Y - q1Z * q2Z;

  return [x, y, z, w];
};

export const rotateVector = (
  v: Vector3Tuple,
  q: QuaternionTuple,
): Vector3Tuple => {
  const qNormalized = normalizeQuaternion(q);
  const qConj = conjugateQuaternion(qNormalized);
  const vQuat: QuaternionTuple = [v[0], v[1], v[2], 0];

  const temp = multiplyQuaternion(qNormalized, vQuat);
  const rotated = multiplyQuaternion(temp, qConj);

  return [rotated[0], rotated[1], rotated[2]];
};

const makeBytesUnitFactory = (power: number) => (bytes: number) =>
  bytes / 1024 ** power;

export const bytesToPB = makeBytesUnitFactory(5);
export const bytesToTB = makeBytesUnitFactory(4);
export const bytesToGB = makeBytesUnitFactory(3);
export const bytesToMB = makeBytesUnitFactory(2);
export const bytesToKB = makeBytesUnitFactory(1);
export const formatByteSize = (bytes: number) => {
  if (bytes < 0) {
    throw new Error('Bytes cannot be negative');
  }

  if (bytes < 1024) {
    return [bytes, 'B'] as [number, 'B' | 'KB' | 'MB' | 'GB' | 'TB' | 'PB'];
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / Math.pow(1024, exponent);

  return [value, units[exponent]] as [
    number,
    'B' | 'KB' | 'MB' | 'GB' | 'TB' | 'PB',
  ];
};

export const eulerToQuaternion = ([x, y, z]: Vector3Tuple | EulerTuple) => {
  const cx = Math.cos(x / 2);
  const sx = Math.sin(x / 2);
  const cy = Math.cos(y / 2);
  const sy = Math.sin(y / 2);
  const cz = Math.cos(z / 2);
  const sz = Math.sin(z / 2);

  const qw = cx * cy * cz + sx * sy * sz;
  const qx = sx * cy * cz - cx * sy * sz;
  const qy = cx * sy * cz + sx * cy * sz;
  const qz = cx * cy * sz - sx * sy * cz;

  return [qx, qy, qz, qw] as QuaternionTuple;
};

export const quaternionToEuler = ([x, y, z, w]: QuaternionTuple) => {
  const sinrCosp = 2 * (w * x + y * z);
  const cosrCosp = 1 - 2 * (x * x + y * y);
  const roll = Math.atan2(sinrCosp, cosrCosp);

  const sinp = 2 * (w * y - z * x);
  const pitch =
    Math.abs(sinp) >= 1 ? (Math.sign(sinp) * Math.PI) / 2 : Math.asin(sinp);

  const sinyCosp = 2 * (w * z + x * y);
  const cosyCosp = 1 - 2 * (y * y + z * z);
  const yaw = Math.atan2(sinyCosp, cosyCosp);

  return [roll, pitch, yaw, 'XYZ'] as EulerTuple;
};
